'use client'

import { useMemo, useCallback, memo } from 'react'
import { CompactTag } from '@/ui/compact-tag'
import { CakeIcon, CaretRightIcon } from '@phosphor-icons/react'
import {
  PERSON_CATEGORY_COLORS,
  PERSON_CATEGORY_LABELS,
  PERSON_AVATAR_COLORS,
} from './category-colors'
import { formatRelativeTime, formatDate, getNextBirthday } from '@/lib/date'
import { cn } from '@/lib/tailwind'
import type { PersonWithLastInteraction } from './select-people'

export const PersonRow = memo(function PersonRow({
  person,
  isSelected,
  isFocused,
  onSelect,
  onPrefetch,
}: {
  person: PersonWithLastInteraction
  isSelected: boolean
  isFocused: boolean
  onSelect: (id: string) => void
  onPrefetch: (id: string) => void
}) {
  const upcomingBirthday = useMemo(
    () => getNextBirthday((person.metadata.birthday as string) || null),
    [person.metadata.birthday],
  )

  const handleClick = useCallback(() => {
    onSelect(person.id)
  }, [onSelect, person.id])

  const handleMouseEnter = useCallback(() => {
    onPrefetch(person.id)
  }, [onPrefetch, person.id])

  return (
    <button
      id={`person-${person.id}`}
      type="button"
      role="option"
      aria-selected={isSelected}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      className={cn(
        'group flex w-full items-center gap-2.5 py-2 pr-2 pl-3 text-left transition-colors sm:gap-3 sm:py-2.5 sm:pr-3 sm:pl-4',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset',
        isSelected && 'bg-accent/50',
        isFocused && !isSelected && 'bg-muted/30',
        !isSelected && !isFocused && 'hover:bg-muted/40',
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium sm:h-9 sm:w-9 sm:text-sm',
          PERSON_AVATAR_COLORS[
            person.metadata.category as 'family' | 'friend' | 'work'
          ],
          'text-foreground',
        )}
        aria-hidden="true"
      >
        {person.title.charAt(0).toUpperCase()}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-medium">{person.title}</span>
          {upcomingBirthday && (
            <span
              className="text-warning flex shrink-0 items-center gap-0.5 text-[11px]"
              title={`Birthday ${upcomingBirthday}`}
            >
              <CakeIcon className="h-3 w-3" aria-hidden="true" />
              <span className="sr-only">Birthday </span>
              <span className="hidden sm:inline">{upcomingBirthday}</span>
            </span>
          )}
        </div>
        <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-[11px]">
          <CompactTag
            variant="colored"
            className={cn(
              'shrink-0',
              PERSON_CATEGORY_COLORS[
                person.metadata.category as 'family' | 'friend' | 'work'
              ],
            )}
          >
            {
              PERSON_CATEGORY_LABELS[
                person.metadata.category as 'family' | 'friend' | 'work'
              ]
            }
          </CompactTag>
          {person.lastInteraction ? (
            <span
              className="truncate"
              title={formatDate(person.lastInteraction)}
            >
              <span className="sr-only">Last interaction </span>
              {formatRelativeTime(person.lastInteraction)}
            </span>
          ) : (
            <span className="text-muted-foreground/60 truncate">
              No interactions
            </span>
          )}
        </div>
      </div>

      {/* Arrow indicator */}
      <CaretRightIcon
        className={cn(
          'h-4 w-4 shrink-0 transition-opacity',
          isSelected || isFocused
            ? 'text-foreground opacity-100'
            : 'text-muted-foreground opacity-0 group-hover:opacity-100',
        )}
        aria-hidden="true"
      />
    </button>
  )
})
